import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, thumbUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import { Heart } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function GroupPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, hasItem } = useCart();
  const { toggleFav, isFav } = useFavourites();
  const [group, setGroup] = useState(null);
  const [event, setEvent] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [numFilter, setNumFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [g, imgs] = await Promise.all([
          api.get(`/groups/${id}`),
          api.get(`/images?group_id=${id}`),
        ]);
        if (cancelled) return;
        setGroup(g.data.group);
        setEvent(g.data.event);
        setImages(imgs.data);
      } catch {
        if (!cancelled) setGroup(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="pt-32 text-center text-white/50 text-sm uppercase tracking-[0.3em]">Loading…</div>;
  const normFilter = numFilter.replace(/^0+/, "") || numFilter;
  const shownImages = numFilter
    ? images.filter((i) => (i.race_numbers || []).includes(normFilter))
    : images;
  if (!group) return (
    <div className="pt-32 text-center">
      <p className="text-white/50 text-sm uppercase tracking-[0.3em]">Class not found.</p>
      <button onClick={() => navigate("/")} className="mt-6 text-white underline-offset-4 hover:underline text-sm">Back to events</button>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="group-page">
      <div className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-white/50">
        <Link to="/" className="hover:text-white" data-testid="crumb-events">Events</Link>
        <span className="text-white/30">/</span>
        {event && (
          <>
            <Link to={`/event/${event.id}`} className="hover:text-white" data-testid="crumb-event">
              {event.track}
            </Link>
            <span className="text-white/30">/</span>
          </>
        )}
        <span className="text-white">{group.name}</span>
      </div>

      <div className="mt-8 border-b border-white/10 pb-10 flex items-end justify-between flex-wrap gap-6">
        <div>
          {event && <p className="text-xs uppercase tracking-[0.3em] text-white/40">{fmtDate(event.date)} · {event.track}</p>}
          <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mt-3" data-testid="group-name">{group.name}</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-6">
            {numFilter ? `${shownImages.length} of ${images.length}` : images.length} {images.length === 1 ? "photo" : "photos"}
          </p>
        </div>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 pm-orange font-display text-lg pointer-events-none">#</span>
          <input
            type="text"
            inputMode="numeric"
            value={numFilter}
            onChange={(e) => setNumFilter(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="Race number"
            className="w-44 bg-black/60 border border-white/25 pl-9 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--pm-orange)] transition-colors"
            data-testid="group-number-filter"
          />
        </div>
      </div>

      {shownImages.length === 0 ? (
        <p className="text-white/50 text-sm py-16 text-center" data-testid="group-no-photos">
          {images.length === 0
            ? "No photos in this class yet."
            : `No photos tagged #${numFilter} in this class — some numbers are hard to read automatically, so browse the full gallery too.`}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-10">
          {shownImages.map((img, idx) => (
            <div
              key={img.id}
              className="relative gallery-card block aspect-[4/5] fade-up"
              style={{ animationDelay: `${idx * 40}ms` }}
              data-testid={`group-photo-${img.id}`}
            >
              <Link to={`/image/${img.id}`} className="block w-full h-full">
                <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="reveal">
                  <div>
                    <h3 className="font-serif text-2xl text-white leading-tight">{img.title}</h3>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{formatPrice(img.price, img.currency)}</p>
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-3 py-2">View</span>
                </div>
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(img); }}
                className={`absolute top-3 left-3 z-10 p-2.5 transition ${
                  isFav(img.id)
                    ? "bg-[var(--pm-orange)] text-black"
                    : "bg-black/70 text-white border border-white/40 hover:text-[var(--pm-orange)] hover:border-[var(--pm-orange)]"
                }`}
                data-testid={`fav-toggle-${img.id}`}
                aria-label={isFav(img.id) ? "Remove from favourites" : "Save to favourites"}
              >
                <Heart size={15} fill={isFav(img.id) ? "currentColor" : "none"} />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); addItem(img); }}
                className={`absolute top-3 right-3 z-10 text-xs uppercase tracking-[0.2em] font-bold px-3 py-2 transition ${
                  hasItem(img.id)
                    ? "bg-[var(--pm-orange)] text-black"
                    : "bg-black/70 text-white border border-white/40 hover:bg-[var(--pm-orange)] hover:text-black hover:border-[var(--pm-orange)]"
                }`}
                data-testid={`add-to-cart-${img.id}`}
                aria-label="Add to basket"
              >
                {hasItem(img.id) ? "✓ In basket" : "+ Basket"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
